
// src/components/pos/PaymentModal.js
import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    Alert
} from '@mui/material';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 600 },
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const PaymentModal = ({ open, onClose, total, customers, onFinalizeSale }) => {
    // --- ESTADOS INTERNOS DEL MODAL ---
    const [paymentMethod, setPaymentMethod] = useState('Efectivo');
    const [cashReceived, setCashReceived] = useState('');
    const [change, setChange] = useState(0);
    const [cardTransactionId, setCardTransactionId] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [error, setError] = useState('');

    // --- EFECTOS ---

    // Efecto para calcular el cambio en tiempo real para pagos en efectivo
    useEffect(() => {
        if (paymentMethod === 'Efectivo' && cashReceived) {
            const received = parseFloat(cashReceived);
            if (!isNaN(received) && received >= total) {
                setChange(received - total);
            } else {
                setChange(0);
            }
        } else {
            setChange(0);
        }
    }, [cashReceived, total, paymentMethod]);

    // Efecto para limpiar (resetear) el estado del modal cada vez que se abre
    useEffect(() => {
        if (open) {
            setPaymentMethod('Efectivo');
            setCashReceived('');
            setCardTransactionId('');
            setSelectedCustomer('');
            setError('');
            setChange(0);
        }
    }, [open]);

    // --- MANEJADOR PRINCIPAL ---
    const handleFinalize = () => {
        setError('');
        let paymentData = {};
        let saleCustomerId = null; // Inicia como null por defecto

        switch (paymentMethod) {
            case 'Efectivo':
                if (!cashReceived || parseFloat(cashReceived) < total) {
                    setError('El monto recibido es insuficiente.');
                    return;
                }
                paymentData = {
                    method: 'Efectivo',
                    amount: total,
                    cash_received: parseFloat(cashReceived),
                    cash_change: change
                };
                break;

            case 'Tarjeta Bancaria':
                if (!cardTransactionId) {
                    setError('El ID de transacción de la tarjeta es obligatorio.');
                    return;
                }
                paymentData = {
                    method: 'Tarjeta Bancaria',
                    amount: total,
                    card_transaction_id: cardTransactionId
                };
                break;

            case 'Credito':
                if (!selectedCustomer) {
                    setError('Se debe seleccionar un cliente para una venta a crédito.');
                    return;
                }
                const customer = customers.find(c => c.id === selectedCustomer);
                if (!customer) {
                    setError('Cliente no encontrado. Por favor, recarga la página.');
                    return;
                }
                if (!customer.is_credit_authorized) {
                    setError('Este cliente no tiene el crédito autorizado.');
                    return;
                }
                const newBalance = (Number(customer.credit_balance) || 0) + total;
                if (newBalance > (Number(customer.credit_limit) || 0)) {
                    setError(`El límite de crédito ($${Number(customer.credit_limit).toFixed(2)}) sería excedido. Saldo actual: $${Number(customer.credit_balance).toFixed(2)}.`);
                    return;
                }
                saleCustomerId = selectedCustomer; // Asignamos el ID del cliente a la venta
                paymentData = { method: 'Credito', amount: total };
                break;

            case 'Transferencia':
            case 'Beercoin':
            case 'Puntos':
                paymentData = { method: paymentMethod, amount: total };
                break;
            
            default:
                setError('Método de pago no válido.');
                return;
        }

        // Llamar a la función del padre con los datos correctos
        onFinalizeSale([paymentData], saleCustomerId);
    };

    // --- RENDERIZADO DEL COMPONENTE ---
    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <Typography variant="h4" component="h2" align="center" gutterBottom>
                    Total a Pagar: ${total.toFixed(2)}
                </Typography>

                <FormControl fullWidth margin="normal">
                    <InputLabel id="payment-method-label">Método de Pago</InputLabel>
                    <Select
                        labelId="payment-method-label"
                        value={paymentMethod}
                        label="Método de Pago"
                        onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                        <MenuItem value={'Efectivo'}>Efectivo</MenuItem>
                        <MenuItem value={'Tarjeta Bancaria'}>Tarjeta Bancaria</MenuItem>
                        <MenuItem value={'Transferencia'}>Transferencia</MenuItem>
                        <MenuItem value={'Credito'}>Crédito</MenuItem>
                        <MenuItem value={'Beercoin'}>Beercoin</MenuItem>
                        <MenuItem value={'Puntos'}>Puntos de Recompensa</MenuItem>
                    </Select>
                </FormControl>

                {/* Campos condicionales que aparecen según el método de pago */}

                {paymentMethod === 'Efectivo' && (
                    <>
                        <TextField
                            margin="normal" required fullWidth autoFocus
                            label="Monto Recibido" name="cashReceived" type="number"
                            value={cashReceived} onChange={(e) => setCashReceived(e.target.value)}
                        />
                        {change > 0 && (
                            <Typography variant="h6" color="secondary.main" align="center" sx={{ mt: 2 }}>
                                Cambio a devolver: ${change.toFixed(2)}
                            </Typography>
                        )}
                    </>
                )}

                {paymentMethod === 'Tarjeta Bancaria' && (
                    <TextField
                        margin="normal" required fullWidth
                        label="ID de Transacción de Terminal" name="cardTransactionId"
                        value={cardTransactionId} onChange={(e) => setCardTransactionId(e.target.value)}
                    />
                )}

                {paymentMethod === 'Credito' && (
                    <FormControl fullWidth margin="normal" required>
                        <InputLabel id="customer-select-label">Cliente</InputLabel>
                        <Select
                            labelId="customer-select-label"
                            value={selectedCustomer}
                            label="Cliente"
                            onChange={(e) => setSelectedCustomer(e.target.value)}
                        >
                            {/* Verificamos que 'customers' sea un array antes de mapear */}
                            {Array.isArray(customers) && customers.map((c) => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Button variant="outlined" color="secondary" onClick={onClose} sx={{ width: '50%' }}>
                        Cancelar
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleFinalize} sx={{ width: '50%' }}>
                        Finalizar Venta
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default PaymentModal;